import { useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { ApiFeaturedListResponse } from "shared/types/selection/ApiSelectionFeaturedList";
import {
  useCreateFsl,
  useDeleteFSL,
  useGetAvailableTagsForDefaultFSLs,
  useGetFSLById,
  useGetSearchCriteriaForFSLs,
  useUpdateFsl
} from "../dataloader";
import { useGetCardsConfigurations } from "../../cards/list/dataloader";
import SelectableType from "shared/enums/SelectableType";
import { FormFieldOption } from "shared/form/fields/types";
import * as yup from "yup";
import { Copies, useCopies } from "shared/hooks/useCopies";
import { ApiSelectionFeaturedListConfig } from "shared/types/selection/ApiSelectionFeaturedListConfig";
import keyBy from "lodash/keyBy";
import { useStaticData } from "context/staticData";
import SchemaService from "shared/form/renderer/SchemaService";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { useCommonFeatures } from "context/commonFeatures";
import { getTransformedCriteriaForApi, validateQueryInValidationSchema } from "./components/searchCriteria";
import { AnyObject } from "yup/lib/object";
import useInitAdminScreen from "apps/console/hooks/useInitAdminScreen";
import times from "lodash/times";
import { FeaturedListDetailProps, FeaturedSelectionServiceFormHelper } from "./types";
import { FormikProps } from "formik";
import { useAuthority } from "context/authority";
import React from "react";
import { getListByFeaturedListContextType } from "./components/SystemGeneratedList/ListFactory";

export function getYupTestForQuery(copies: Copies, { isOptional }: { isOptional: boolean }) {
  return function yupQueryTest(criteria: any, context: yup.TestContext<AnyObject>) {
    if (isOptional && criteria === null) {
      return true;
    }
    const validation = validateQueryInValidationSchema(criteria, copies);
    if (!validation.isValid) {
      return context.createError({ message: validation.message });
    }
    return true;
  };
}

export function useFeaturedListDetailsService({
  contextType,
  criteriaConfigType,
  selectionType,
  contextId,
  contextEntityType,
  fslHelper
}: FeaturedListDetailProps) {
  const { hasProgramWriteAuthority } = useAuthority();
  const { fslId = "" } = useParams();
  const { data, isSuccess: hasFslSuccess } = useGetFSLById(fslId);
  const { data: cards = [], isSuccess: hasCards } = useGetCardsConfigurations(contextId, contextEntityType);
  const { data: tags } = useGetAvailableTagsForDefaultFSLs(contextType, contextId, contextEntityType, selectionType);

  const {
    data: searchCriteriaConfigFieldOptions = {} as ApiSelectionFeaturedListConfig,
    isSuccess: hasFieldOptionsSuccess
  } = useGetSearchCriteriaForFSLs(criteriaConfigType);

  const { isCreateMode } = useInitAdminScreen("fslId");
  const { mutateAsync: createFsl } = useCreateFsl(contextId);
  const { mutateAsync: updateFsl } = useUpdateFsl(contextId);
  const { mutateAsync: deleteFsl } = useDeleteFSL(contextId);
  const { initConfirmationAlert } = useCommonFeatures();
  const navigation = useNavigateWrapper();
  const formRef = useRef<FormikProps<any>>(null);

  const hasAllConfigLoaded = (hasFslSuccess || isCreateMode) && hasCards && hasFieldOptionsSuccess;

  const staticData = useStaticData();
  const copies = useCopies();

  const handleDeleteFSL = useCallback(async () => {
    initConfirmationAlert({
      message: copies.get("action.confirmation"),
      handleConfirm: async () => {
        try {
          await deleteFsl({ params: { id: fslId } });
          navigation.navigateBack();
        } catch (e) {}
      }
    });
  }, [deleteFsl, fslId, initConfirmationAlert, navigation, copies]);

  const formHelper: FeaturedSelectionServiceFormHelper = useMemo(() => {
    const List = getListByFeaturedListContextType(contextType);
    const validationSchema = List.getValidationSchema(copies);
    async function handleSubmit({ actorCriteria, recipientCriteria, ...values }: any) {
      try {
        const createUpdateFSLConfigApiObject = {
          ...values,
          actorCriteria: getTransformedCriteriaForApi(actorCriteria),
          recipientCriteria: getTransformedCriteriaForApi(recipientCriteria),
          selectionType,
          contextType
        };

        try {
          if (values.id) {
            await updateFsl(createUpdateFSLConfigApiObject);
          } else {
            await createFsl(createUpdateFSLConfigApiObject);
          }
          navigation.navigateBack();
        } catch (e) {}
      } catch (error) {}
    }
    const availabilityTypesOption = SchemaService.getOptionsListFromStaticData(
      staticData.selectionAvailabilityTypesOption
    );

    const matchCriteriaTypeOptions = [
      ...SchemaService.getOptionsListFromStaticData(staticData.selectionMatchCriteriaTypeOptions)
    ];
    const groupByTypeOptions = SchemaService.getOptionsListFromStaticData(staticData.selectableTypeForFSL);
    const groupByCardConfigOptions = cards
      .filter(card => card.selectableType === SelectableType.ORGANIZATION)
      .map(card => ({ label: card.name, value: card.id }));

    const selectableCardConfigOptions = cards
      .filter(card => card.selectableType === SelectableType.PARTICIPANT)
      .map(card => ({ label: card.name, value: card.id }));

    const getSelectableCardConfigOptions = (selectableType: SelectableType) => {
      return cards
        .filter(card => card.selectableType === selectableType)
        .map(card => ({ label: card.name, value: card.id }));
    };

    const searchCriteriaConfigFieldOptionsMap = keyBy(searchCriteriaConfigFieldOptions.fields || [], "id");

    const displayOrderOptions = times(99, index => ({
      label: (index + 1).toString(),
      value: (index + 1).toString()
    })) as Array<FormFieldOption>;

    const disabled = fslHelper.isFSLConfigurationEditDisabled(data);
    const fslTypeOptions = fslHelper.getFSLTypeOptions(staticData, isCreateMode, contextType, selectionType);

    const availableTagsOptions = SchemaService.getOptionsListFromStaticData(staticData.programParticipantTag).filter(
      item => tags?.availableTags?.includes(item.value as any) || data?.tags.includes(item.value as any)
    );
    const availableSubRoleOptions = SchemaService.getOptionsListFromStaticData(staticData.participantSubRole).filter(
      item => tags?.availableSubRoles?.includes(item.value as any) || data?.subRoles.includes(item.value as any)
    );
    const allTagsOptions = SchemaService.getOptionsListFromStaticData(staticData.programParticipantTag);
    const allAvailableSubRoleOptions = SchemaService.getOptionsListFromStaticData(staticData.participantSubRole);

    const selectableTypeOptions = SchemaService.getOptionsListFromStaticDataMap({
      selectedMapKey: fslHelper.selectedMapKey,
      staticDataMap: staticData.programTypeToSelectableTypeMap,
      staticDataOptions: staticData.selectableType
    });

    return {
      initialValues: List.getInitialValues(contextId, contextEntityType, data) as any as ApiFeaturedListResponse,
      handleSubmit,
      availabilityTypesOption,
      groupByCardConfigOptions,
      selectableCardConfigOptions,
      matchCriteriaTypeOptions,
      groupByTypeOptions,
      validationSchema,
      searchCriteriaConfigFieldOptions,
      searchCriteriaConfigFieldOptionsMap,
      displayOrderOptions,
      disabled,
      fslTypeOptions,
      availableTagsOptions,
      allTagsOptions,
      selectableTypeOptions,
      getSelectableCardConfigOptions,
      availableSubRoleOptions,
      allAvailableSubRoleOptions
    };
  }, [
    contextType,
    copies,
    staticData,
    cards,
    searchCriteriaConfigFieldOptions,
    fslHelper,
    data,
    isCreateMode,
    selectionType,
    contextId,
    contextEntityType,
    navigation,
    updateFsl,
    createFsl,
    tags?.availableTags,
    tags?.availableSubRoles
  ]);

  const isFormReadOnly = React.useMemo(() => !hasProgramWriteAuthority(), [hasProgramWriteAuthority]);

  return {
    data,
    formHelper,
    handleDeleteFSL,
    isCreateMode,
    hasAllConfigLoaded,
    isFormReadOnly,
    formRef,
    criteriaConfigType
  };
}
